import React from 'react';
import { PackageIcon } from '../../ui/icons';
import { formatRuleBadge } from '../../utils/priceBreakdown';
import { useTranslation } from '../../translations';
import { formatMoney } from '../../utils/format';

// P7.5: shared presentational pieces for price visibility in the three booking
// flows (GenerateOffer, reservation.js, calendar ReservationFormView). Every
// component renders its plain fallback (or nothing) when given no view-model,
// so the no-quote UI stays identical to the pre-P7.5 markup.

const badgeStyle = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: 'var(--info)',
  background: 'var(--info-bg)',
  border: '1px solid var(--info-border)',
  borderRadius: 999,
  padding: '1px 8px',
  whiteSpace: 'nowrap',
};

// Daily rate for one unit: struck-through base + effective when they differ
// ("śr." prefix when the per-day rate varied inside the range). Falls back to
// the caller's plain text when there is no view-model or no difference.
//
// CELOWO bez formatMoney/t() tutaj: `cypress/e2e/price-visibility.cy.js` czyta ten
// tekst przez `parseFloat` (usuwajac literalnie 'śr.') zeby porownac stawke bazowa
// i efektywna liczbowo. `formatMoney` w lokalizacji PL wstawia przecinek dziesietny,
// ktorego `parseFloat` nie rozumie — zmiana tu wymaga ROWNOCZESNEJ zmiany testu na
// parsowanie liczby odpornej na przecinek/kropke, inaczej test cicho porownuje
// obcięte wartosci. Zostawione jak bylo, dopoki ktos nie zrobi obu na raz.
export function ItemRate({ item, baseText }) {
  if (!item || !item.differsFromBase) return <>{baseText}</>;
  return (
    <span data-cy="effective-rate">
      <s style={{ opacity: 0.55, fontWeight: 400, marginRight: 6 }}>{item.baseDaily.toFixed(2)} zł</s>
      {item.isAverage ? 'śr. ' : ''}{item.effectiveDaily.toFixed(2)} zł/dzień
    </span>
  );
}

// Badges of the price rules that actually fired for this unit (or, since N.2, this bundle).
export function RuleBadges({ item, align = 'flex-end' }) {
  const { t } = useTranslation();
  if (!item || !item.appliedRules || !item.appliedRules.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: align, marginTop: 3 }}>
      {item.appliedRules.map((rule) => (
        <span key={rule.id} data-cy="price-rule-badge" style={badgeStyle}>
          {t('priceBreakdown.ruleLabel', { rule: formatRuleBadge(rule) })}
        </span>
      ))}
    </div>
  );
}

// Frame around the rows of one matched bundle, labelled with its name, set price
// and the price rules that changed it (N.2). When the rate differed between days
// the amount is an average, so it says "śr." like ItemRate does. Pass-through
// (no extra DOM) when the group is not a bundle.
//
// Nie asertowane numerycznie w E2E (tylko `data-cy="bundle-group"` bez parsowania
// wartosci), wiec — inaczej niz `ItemRate` — moze bezpiecznie uzywac formatMoney.
export function BundleFrame({ bundle, children }) {
  const { t, currentLanguage } = useTranslation();
  if (!bundle) return <>{children}</>;
  return (
    <div
      data-cy="bundle-group"
      style={{
        border: '1.5px dashed rgba(139, 92, 246, 0.55)',
        borderRadius: 10,
        padding: '8px 10px',
        marginBottom: 6,
      }}
    >
      <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--primary)', marginBottom: 6 }}>
        <PackageIcon size={14} />{' '}
        {bundle.isAverage
          ? t('priceBreakdown.bundleNameAverage', { name: bundle.name, price: formatMoney(bundle.dailyRate, { locale: currentLanguage }) })
          : t('priceBreakdown.bundleName', { name: bundle.name, price: formatMoney(bundle.dailyRate, { locale: currentLanguage }) })}
        <RuleBadges item={bundle} align="flex-start" />
      </div>
      {children}
    </div>
  );
}

// Subtle "recalculating" note shown while the debounced quote is in flight.
// Renders nothing otherwise — including after a quote error (local fallback).
export function QuotePendingNote({ pending }) {
  const { t } = useTranslation();
  if (!pending) return null;
  return (
    <span data-cy="quote-pending" style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>
      {t('priceBreakdown.recalculating')}
    </span>
  );
}

// Extra Podsumowanie rows under "Cena sprzętu": local base sum → price-rules
// adjustment → bundle saving (only rows that are non-zero; nothing at all when
// the quote equals the local calc). Rendered with the caller's styled
// components so each page keeps its own look.
//
// `breakdown-rules`/`breakdown-bundles` sa w E2E asertowane tylko widocznoscia i
// znakiem '+' (nie parseFloat), wiec formatMoney jest tu bezpieczny.
export function SummaryBreakdownRows({ breakdown, Row, Label, Value }) {
  const { t, currentLanguage } = useTranslation();
  if (!breakdown || !breakdown.hasAdjustments) return null;
  const muted = { fontSize: '0.85rem', color: 'var(--text-muted)' };
  const showRules = Math.abs(breakdown.ruleAdjustment) >= 0.01;
  const showBundles = Math.abs(breakdown.bundleSavings) >= 0.01;
  return (
    <>
      <Row data-cy="breakdown-base">
        <Label style={muted}>&nbsp;&nbsp;{t('priceBreakdown.baseSum')}</Label>
        <Value style={muted}>{formatMoney(breakdown.baseTotal, { locale: currentLanguage })}</Value>
      </Row>
      {showRules && (
        <Row data-cy="breakdown-rules">
          <Label style={muted}>&nbsp;&nbsp;{t('priceBreakdown.ruleAdjustment')}</Label>
          <Value style={muted}>
            {breakdown.ruleAdjustment > 0 ? '+' : '−'}{formatMoney(Math.abs(breakdown.ruleAdjustment), { locale: currentLanguage })}
          </Value>
        </Row>
      )}
      {showBundles && (
        <Row data-cy="breakdown-bundles">
          <Label style={muted}>&nbsp;&nbsp;{t('priceBreakdown.bundleSaving')}</Label>
          <Value style={muted}>
            {breakdown.bundleSavings >= 0 ? '−' : '+'}{formatMoney(Math.abs(breakdown.bundleSavings), { locale: currentLanguage })}
          </Value>
        </Row>
      )}
    </>
  );
}
